import {
  Automation,
  ClimateCondition,
  LoggerCondition,
  SingleCondition,
  SoilCondition,
} from "../../../api/automation";
import { Logger } from "../../../api/loggers";
import { metricLabels } from "../../../constants/metrics";
import { capitalizeFirstLetter as capitalise } from "../../../helpers";

const formatMetric = (
  metric: (ClimateCondition | SoilCondition | LoggerCondition)["metric"]
): string => {
  const label = metricLabels.get(
    metric as ClimateCondition["metric"] | SoilCondition["metric"]
  );
  if (label) {
    return label;
  }
  if (metric === "vpd") {
    return "VPD";
  }
  return capitalise(metric.replace(/_/g, " "));
};

const formatCondition = (
  condition: SingleCondition,
  loggers: Logger[]
): string => {
  switch (condition.type) {
    case "climate":
    case "soil":
      return `${formatMetric(condition.metric)} is ${
        condition.comparison === "gt" ? "greater than" : "less than"
      } ${condition.threshold}`;
    case "soil_climate":
      return `Root zone moisture is ${
        condition.comparison === "gt" ? "greater than" : "less than"
      } ${condition.threshold_manual ?? condition.threshold_name} `;
    case "logger": {
      const logger = loggers.find((l) => l.id === condition.logger_id);
      return `${
        logger ? `Logger #${logger.logger_id}` : "Unknown Logger"
      } ${formatMetric(condition.metric)} is ${
        condition.comparison === "gt" ? "greater than" : "less than"
      } ${condition.threshold}`;
    }
    case "probe_offline":
      return `Moisture probe has not reported for more than ${condition.threshold_minutes} minutes`;
    case "time":
      return `The time is between ${condition.start} and ${condition.end}`;
  }
};

const flattenConditions = (
  conditions: Automation["data"]["conditions"],
  lastHint?: "AND" | "OR",
  hint: "AND" | "OR" = "AND"
): { condition: SingleCondition; andOr?: "AND" | "OR" }[] => {
  const flattened: { condition: SingleCondition; andOr?: "AND" | "OR" }[] = [];
  conditions.forEach((condition, idx) => {
    switch (condition.type) {
      case "and":
        flattened.push(...flattenConditions(condition.conditions, hint, "AND"));
        break;
      case "or":
        flattened.push(...flattenConditions(condition.conditions, hint, "OR"));
        break;
      default:
        flattened.push({ condition, andOr: idx ? hint : lastHint });
    }
  });
  return flattened;
};

export const AutomationConditionDisplay: React.FC<{
  conditions: Automation["data"]["conditions"];
  loggers: Logger[];
}> = ({ conditions, loggers }) => (
  <ul>
    {flattenConditions(conditions).map(({ condition, andOr }, idx) => {
      const formatted = formatCondition(condition, loggers);
      return (
        <li key={idx}>
          {andOr ? (
            <>
              <strong>{andOr}</strong> {formatted}
            </>
          ) : (
            <>When {formatted.toLowerCase()}</>
          )}
        </li>
      );
    })}
  </ul>
);
